<template>
  <aside class="sidebar">
    <div v-if="isHistoryShown" class="history" ref="history">
      <h3 class="history__heading">
        <img
          @click="isHistoryShown = false"
          class="history__back"
          src="@/assets/icon-back.svg"
        />
        {{ $t('VERSION_HISTORY') }}
      </h3>
      <p class="history__text">
        {{ $t('ARCHIVED_SUMMARY') }}
      </p>

      <version
        v-for="version in versionsList"
        :version="version"
        :type="type"
        :idPending="idPending"
        @install="idPending = $event"
        :key="version[0]"
      />
    </div>

    <template v-else>
      <div v-if="isTweaked" class="block block_yellow">
        <h3 class="block__h3">{{ $t('TWEAKED_VERSION_NOTICE_TITLE') }}</h3>
        <p class="block__text">
          {{ $t('TWEAKED_VERSION_NOTICE') }}
        </p>
        <router-link :to="`/app/${originalType}/${originalId}`">
          {{ $t('VIEW_ORIGINAL_APP') }}
        </router-link>
      </div>
      <div
        v-if="
          (type === 'ios' || type === 'cydia') &&
          currentDevice &&
          currentDevice.is_apple_silicon === 'yes'
        "
        class="block block_blue block_border"
      >
        <html-with-links
          :html="
            $t('APPLE_SILICON_IOS_APPS', {
              buyLink: '/my/buy'
            })
          "
          tag="p"
          class="block__text"
        ></html-with-links>
      </div>

      <div class="block block_pink">
        <h3 class="block__h3">{{ $t('DOWNLOAD_AND_INSTALL') }}</h3>
        <p v-if="appStoreUrl" class="block__text">
          <download-on-appstore :href="appStoreUrl" target="_blank"/>
        </p>
        <template v-if="!linkToInstallError">
          <p class="block__text">{{ $t('COMMUNITY_DOWNLOAD_INSTALLATION_OPTIONS') }}</p>
          <p v-if="!isLastVersionAvailable" class="block__text">
            {{ repo ? $t('NO_REPO_APP_DOWNLOAD_LINK') : $t('NO_LINKS_FOR_ACTUAL_VERSION') }}
          </p>
          <template v-if="repo">
            <version
              :version="versionsList[0]"
              :type="type"
              :idPending="idPending"
              :repo="repo"
              @install="idPending = $event"
            />
          </template>

          <template v-if="!repo">
            <version
              :version="versionsList.find((v) => v[0] === currentVersion)"
              :type="type"
              :idPending="idPending"
              :repo="repo"
              @install="idPending = $event"
            />
            <div class="block__links" v-if="!isLastVersionAvailable">
              <a
                class="block__link"
                href="#"
                @click.prevent="isHistoryShown = true"
              >
                {{ $t('CHECK_ARCHIVE_BUTTON') }}
              </a>
              <template v-if="type === 'ios'">
                <span class="block__links-or">{{ $t('OR') }}</span>
                <a
                  href="#"
                  class="block__link"
                  @click.prevent="requestApp"
                >
                  {{ $t('REQUEST_APP_UPLOAD_BUTTON') }}
                  <span class="block__link-desc">{{ $t('AUTOMATICALLY') }}</span>
                </a>
              </template>
            </div>
          </template>

          <template>
            <p>{{ $t('HISTORY') }}:</p>
            <button class="block__button" @click.prevent="isHistoryShown = true">
              <img class="block__button-icon" src="@/assets/icon-history.svg"/>
              {{
                $t('N_VERSIONS_AVAILABLE', { n: Object.keys(versions).length })
              }}
            </button>
          </template>

          <template v-if="!repo">
            <div>
              <a
                class="block__link"
                target="_blank"
                :href="`https://a.appdb.to/acp.php?action=upload&type=${type}&url=https%3A%2F%2Fitunes.apple.com%2Fee%2Fapp%2Fid${id}`"
              >{{ $t('ADD_MORE_LINKS') }}</a
              >
              <span class="block__caption"
              >({{ $t('UPLOADER_ACCOUNT_REQUIRED') }})</span
              >
            </div>
          </template>
        </template>
        <template v-else>
          <template v-if="copyrightedBy">
            <p class="block__text">{{ $t('COPYRIGHT_CLAIM_NOTICE')}}</p>
            <pre>{{copyrightedBy}}</pre>
          </template>
          <template v-else>
            <html-with-links
              class="block__text"
              :html="$t('LINK_TO_SEE_DOWNLOAD_OPTIONS', {linkUrl: '/link'})"
            />
            <p class="block__text">&nbsp;</p>
            <html-with-links
              class="block__text"
              :html="$t('LINK_DEVICE_DESC',{device: 'iPhone/iPad/iPod/Apple Silicon Mac'})"/>
          </template>
        </template>
      </div>

      <div
        v-if="tweakedApps && tweakedApps.length"
        class="block block_blue block_border"
      >
        <div class="block__text">
          {{ $t('TWEAKS_AVAILABLE', { count: tweakedApps.length }) }}
          <router-link
            v-for="app in tweakedApps"
            class="block__app-link"
            :to="`/app/${app.type}/${app.id}`"
            :key="app.id"
          >{{ app.name }}
          </router-link
          >
        </div>
      </div>
    </template>
  </aside>
</template>

<script>
import compareVersions from 'compare-versions';
import { mapGetters, mapState } from 'vuex';
import HtmlWithLinks from '@/components/HtmlWithLinks';
import DownloadOnAppstore from '@/components/DownloadOnAppstore.vue';
import Version from './Version';

export default {
  components: {
    Version,
    HtmlWithLinks,
    DownloadOnAppstore,
  },
  props: {
    linkToInstallError: Boolean,
    copyrightedBy: String,
    appStoreUrl: String,
    isTweaked: Boolean,
    tweakedApps: Array,
    type: String,
    currentVersion: [String, Number],
    versions: Object,
    id: [String, Number],
    originalId: [String, Number],
    originalType: String,
    repo: Boolean,
  },
  data() {
    return {
      isHistoryShown: false,
      idPending: 0,
    };
  },
  computed: {
    ...mapState(['theme']),
    ...mapGetters(['isDeviceLinked', 'lang']),
    currentDevice() {
      const { config } = this.$store.getters;
      if (Object.keys(config).length) {
        return {
          name: config.name,
          is_apple_silicon: config.is_apple_silicon,
          title: `${config.nice_idevice_model} (${config.ios_version}`,
        };
      }

      return null;
    },
    isLastVersionAvailable() {
      if (!this.currentVersion) {
        return true;
      }
      return Object.keys(this.versions)
        .includes(this.currentVersion);
    },
    versionsList() {
      return Object.entries(this.versions)
        .sort((a, b) => {
          try {
            return -compareVersions(a[0], b[0]);
          } catch {
            try {
              const version1 = a[0].match(/(\d+\.){2}\d+/)[0];
              const version2 = b[0].match(/(\d+\.){2}\d+/)[0];
              return -compareVersions(version1, version2);
            } catch {
              return -1;
            }
          }
        });
    },
  },
  watch: {
    async isHistoryShown() {
      await this.$nextTick();
      if (this.isHistoryShown) {
        const rect = this.$refs.history.getBoundingClientRect();
        if (rect.top < 0) {
          this.$refs.history.scrollIntoView();
        }
      }
    },
  },
  methods: {
    requestApp() {
      this.$api.requestApp(`/app/id${this.id}`)
        .then(() => {
          this.$notify({
            type: 'success',
            text: this.$t('APP_REQUESTED_SUCCESSFULLY'),
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.sidebar {
  overflow-y: scroll;

  @include notebook {
    padding-top: 10px;
  }
}

.block {
  padding: 15px 20px;
  border-radius: 10px;
  margin-bottom: 10px;

  @include notebook {
    padding: 10px;
  }

  &_yellow {
    background-image: var(--light-yellow-gradient);
  }

  &_blue {
    background: var(--lighter-primary);
    border-radius: 10px;
    color: var(--text-primary);
  }

  &_pink {
    background-image: var(--pink-gradient);
  }

  &_border {
    display: flex;

    &:before {
      content: "";
      display: block;
      flex: 0 0 6px;
      margin-right: 10px;
      background-color: #98accd;
      border-radius: 6px;
    }

    .block__text {
      margin: 0;
    }
  }

  &__h3 {
    font-size: 20px;
    color: var(--text-tertiary);
    font-weight: 600;
  }

  &__text {
    margin: 10px 0;
    line-height: 20px;
  }

  &__links {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    max-width: 350px;

    @media (max-width: 350px) {
      flex-direction: column;

      &-or {
        margin: 4px 0;
      }
    }
  }

  &__link {
    display: inline-block;
    padding: 5px 10px;
    border: 1px solid lighten($primary, 20%);
    border-radius: 3px;
    position: relative;

    &-desc {
      position: absolute;
      right: 0;
      pointer-events: none;
      color: var(--text-primary);
      font-size: 12px;
      font-weight: 400;
      bottom: -17px;
    }
  }

  &__app-link {
    display: block;
  }

  &__button {
    color: #fff;
    font-size: 14px;
    background: $primary;
    border-radius: 3px;
    padding: 5px 10px;
    font-weight: 500;
    display: flex;
    align-items: center;
    margin: 8px 0;

    &-icon {
      margin-right: 5px;
    }
  }

  &__caption {
    color: var(--text-primary);
    font-size: 12px;
    display: inline-block;
    margin-left: 8px;
  }
}

.history {
  background-image: linear-gradient(180deg, var(--secondary-bg) 0%, var(--bg) 100%);
  border-radius: 10px;
  padding: 20px;
  max-height: 728px;

  @include notebook {
    padding: 10px;
    max-height: 400px;
  }

  &__heading {
    color: var(--text-tertiary);
    font-size: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
  }

  &__back {
    margin-right: 6px;
    margin-top: 2px;
    cursor: pointer;
  }

  &__text {
    line-height: 20px;
    margin: 10px 0 20px;
  }
}
</style>
